import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRovingTabs } from '../hooks/useRovingTabs';
import { useCloseWatcher } from '../hooks/useCloseWatcher';
import { X, FileText, Download, CornerUpLeft, Image as ImageIcon, Folder, Clock, Printer } from 'lucide-react';
import { iconForFileName } from '../utils/fileIcon';
import { ChannelAttachmentItem } from '../types';
import { dbService } from '../services/dbService';
import { ImageLightbox, LightboxSlide } from './ImageLightbox';
import { isTauri } from '../utils/tauri';
import { formatDateLabel, isDifferentDate } from '../utils/date';
import { formatFileSize as formatSize } from '../utils/formatFileSize';
import { resolveServerUrl } from '../utils/serverUrl';
import { printImages, PRINT_IMAGE_FAILED_TOAST } from '../utils/printImage';
import { useToast } from '../contexts/ToastContext';
import {
  isAttachmentExpired,
  isExpiringSoon,
  formatExpiryLabel,
  EXPIRED_FILE_TEXT,
  EXPIRED_IMAGE_TEXT,
  EXPIRED_IMAGE_SHORT_TEXT,
} from '../utils/attachmentExpiry';

type DrawerTab = 'image' | 'file';

interface AttachmentDrawerProps {
  channelId: number;
  onClose: () => void;
  /** 항목의 원문 메시지 위치로 점프 (호출 측에서 서랍을 닫고 이동) */
  onJumpToMessage: (messageId: number) => void;
}

// 서버 주소 결합은 utils/serverUrl 하나를 쓴다 (ChatWindow·아바타와 공용)
const resolveUrl = resolveServerUrl;

// 표기는 기존 그대로(공백 없는 '1.5MB') 유지 — 공용 유틸의 compact 모드
const formatFileSize = (bytes: number | null) => formatSize(bytes, { compact: true });

const downloadFile = async (url: string, fileName: string) => {
  if (isTauri()) {
    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url);
    return;
  }
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
};

/**
 * 사진·파일 서랍 — 채널별 첨부 모아보기 (카카오톡 서랍 패턴).
 * 우측 슬라이드 패널(모바일은 전체 폭). 열 때마다 fresh fetch, id 커서로 "더 보기" 페이징.
 */
export const AttachmentDrawer: React.FC<AttachmentDrawerProps> = ({
  channelId,
  onClose,
  onJumpToMessage,
}) => {
  const [tab, setTab] = useState<DrawerTab>('image');
  const drawerTabs = useRovingTabs<DrawerTab>(['image', 'file'], tab, setTab);
  const [items, setItems] = useState<ChannelAttachmentItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const { showToast } = useToast();

  const printImage = (url: string) => {
    printImages([url]).catch(() => showToast({ type: 'error', message: PRINT_IMAGE_FAILED_TOAST }));
  };

  const load = useCallback(async (targetTab: DrawerTab, before?: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await dbService.getChannelAttachments(channelId, targetTab, before);
      setItems(prev => (before ? [...prev, ...result.items] : result.items));
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setError('첨부 목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [channelId]);

  // 탭 전환·채널 변경 시 처음부터 다시
  useEffect(() => {
    setItems([]);
    setHasMore(false);
    load(tab);
  }, [tab, load]);

  // ⚠ 만료 항목을 배열에서 빼면 안 된다 — dateGroups의 idx가 flat items 기준이라 어긋난다.
  //    대신 expired를 실어 ImageLightbox가 이미지 대신 안내를 그리게 한다.
  const slides: LightboxSlide[] = tab === 'image'
    ? items.map(item => ({
        src: resolveUrl(item.url),
        alt: item.name,
        downloadUrl: resolveUrl(item.url),
        downloadFilename: item.name,
        expired: isAttachmentExpired(item.expiresAt),
      }))
    : [];

  // 날짜별 그룹핑 (서버가 최신순 정렬을 보장하므로 인접 비교만으로 충분).
  // idx는 flat items 기준 — 라이트박스 인덱스가 slides 배열과 일치해야 한다
  const dateGroups = useMemo(() => {
    const groups: { label: string; entries: { item: ChannelAttachmentItem; idx: number }[] }[] = [];
    items.forEach((item, idx) => {
      const last = groups[groups.length - 1];
      if (!last || isDifferentDate(last.entries[0].item.createdAt, item.createdAt)) {
        groups.push({ label: formatDateLabel(item.createdAt), entries: [{ item, idx }] });
      } else {
        last.entries.push({ item, idx });
      }
    });
    return groups;
  }, [items]);

  // ESC로 닫기 — 라이트박스가 열려 있으면 라이트박스가 먼저 닫히도록 양보한다
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || lightbox.open) return;
      e.stopPropagation();
      // 처리한 Esc가 닫기 요청(CloseWatcher)으로 번지면 화면 단계 watcher까지 닫힌다
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, lightbox.open]);

  // 모바일 뒤로가기로 닫기. 라이트박스는 나중에 열리므로 그쪽 watcher가 먼저 닫힌다(생성 역순)
  useCloseWatcher(true, onClose);

  return (
    <>
      {/* 배경·서랍 모두 PC앱 제목 표시줄 아래에서 시작한다(TitleBar의 --titlebar-h 주석) */}
      <div className="fixed inset-x-0 bottom-0 top-[var(--titlebar-h,0px)] bg-black/40 z-40" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="사진·파일 서랍" className="fixed bottom-0 top-[var(--titlebar-h,0px)] right-0 z-50 w-full sm:w-96 bg-white shadow-xl flex flex-col safe-area-top">
        {/* 헤더 */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100 shrink-0">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Folder size={18} className="text-slate-400" />
            서랍
          </h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            title="닫기"
            aria-label="서랍 닫기"
          >
            <X size={20} />
          </button>
        </div>

        {/* 탭 */}
        <div {...drawerTabs.tablistProps} aria-label="첨부 종류" className="flex border-b border-slate-100 shrink-0">
          {([['image', '사진', ImageIcon], ['file', '파일', FileText]] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              {...drawerTabs.tabProps(key)}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>

        {/* 목록 */}
        <div className="flex-1 overflow-y-auto">
          {error ? (
            <p className="px-4 py-8 text-sm text-brand-600 text-center">{error}</p>
          ) : items.length === 0 && !isLoading ? (
            <p className="px-4 py-8 text-sm text-slate-400 text-center">
              {tab === 'image' ? '주고받은 사진이 없습니다' : '주고받은 파일이 없습니다'}
            </p>
          ) : tab === 'image' ? (
            dateGroups.map(group => (
              <div key={group.entries[0].item.id}>
                <p className="px-3 pt-3 pb-1.5 text-xs font-medium text-slate-500">{group.label}</p>
                <div className="grid grid-cols-3 gap-0.5 px-0.5">
                  {group.entries.map(({ item, idx }) => (
                    <div key={item.id} className="relative group aspect-square">
                      {isAttachmentExpired(item.expiresAt) ? (
                        <div
                          className="w-full h-full flex flex-col items-center justify-center gap-1 px-1 bg-slate-100 text-center text-[10px] leading-tight text-slate-400"
                          title={EXPIRED_IMAGE_TEXT}
                        >
                          <Clock size={16} />
                          {EXPIRED_IMAGE_SHORT_TEXT}
                        </div>
                      ) : (
                        <button
                          onClick={() => setLightbox({ open: true, index: idx })}
                          className="w-full h-full"
                          title={item.name}
                        >
                          <img
                            src={resolveUrl(item.url)}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover bg-slate-100"
                          />
                        </button>
                      )}
                      {!isAttachmentExpired(item.expiresAt) && (
                        <button
                          onClick={() => printImage(resolveUrl(item.url))}
                          className="absolute top-1 right-10 w-8 h-8 flex items-center justify-center rounded-md bg-black/55 text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          title="인쇄"
                          aria-label="인쇄"
                        >
                          <Printer size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => onJumpToMessage(item.messageId)}
                        className="absolute top-1 right-1 w-8 h-8 flex items-center justify-center rounded-md bg-black/55 text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        title="원문으로 이동"
                      >
                        <CornerUpLeft size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            dateGroups.map(group => (
              <div key={group.entries[0].item.id}>
                <p className="px-4 pt-3 pb-1 text-xs font-medium text-slate-500">{group.label}</p>
                <div className="divide-y divide-slate-50">
                  {group.entries.map(({ item }) => {
                    const expired = isAttachmentExpired(item.expiresAt);
                    const expiryLabel = formatExpiryLabel(item.expiresAt);
                    const TypeIcon = iconForFileName(item.name);
                    return (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                      <span className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                        {expired
                          ? <Clock size={18} className="text-slate-400" />
                          : <TypeIcon size={18} className="text-slate-400" />}
                      </span>
                      <button
                        disabled={expired}
                        onClick={() => downloadFile(resolveUrl(item.url), item.name)}
                        className="flex-1 min-w-0 text-left disabled:cursor-not-allowed"
                        title={expired ? EXPIRED_FILE_TEXT : '다운로드'}
                      >
                        <span className={`block text-sm truncate ${expired ? 'text-slate-400' : 'text-slate-700'}`}>{item.name}</span>
                        <span className={`block text-xs mt-0.5 truncate ${
                          expired || isExpiringSoon(item.expiresAt) ? 'text-brand-700' : 'text-slate-400'
                        }`}>
                          {expired
                            ? EXPIRED_FILE_TEXT
                            : [formatFileSize(item.size), expiryLabel, item.senderName].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                      <button
                        disabled={expired}
                        onClick={() => downloadFile(resolveUrl(item.url), item.name)}
                        className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-100 shrink-0 disabled:opacity-40 disabled:hover:text-slate-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title={expired ? EXPIRED_FILE_TEXT : '다운로드'}
                      >
                        <Download size={15} />
                      </button>
                      <button
                        onClick={() => onJumpToMessage(item.messageId)}
                        className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 hover:text-brand-600 hover:bg-slate-100 shrink-0"
                        title="원문으로 이동"
                      >
                        <CornerUpLeft size={15} />
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {isLoading && (
            <p className="px-4 py-4 text-sm text-slate-400 text-center">불러오는 중...</p>
          )}
          {hasMore && !isLoading && (
            <div className="flex justify-center py-3">
              <button
                onClick={() => load(tab, items[items.length - 1]?.id)}
                className="px-4 py-2 text-sm text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                더 보기
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 사진 라이트박스 */}
      <ImageLightbox
        open={lightbox.open}
        slides={slides}
        index={lightbox.index}
        onClose={() => setLightbox(l => ({ ...l, open: false }))}
        onIndexChange={(index) => setLightbox(l => ({ ...l, index }))}
        onDownload={(slide) => downloadFile(slide.downloadUrl, slide.downloadFilename)}
        onPrint={(slide) => printImage(slide.downloadUrl)}
      />
    </>
  );
};
